import React from 'react';
import { Box, Chip, Tooltip, Typography } from '@mui/material';
import { formatQty } from '../../utils/quantityFormat';

interface Props {
  quantity: number;
  parLevel?: number;
  reorderPoint?: number;
  /**
   * RESERVATION-VISIBILITY.1b — ACTIVE holds against this quantity, pooled
   * across every venue that can reach the stock. Omit where reservations are
   * not in play; the badge then renders exactly as before.
   */
  reserved?: number;
  size?: 'small' | 'medium';
}

/**
 * Displays current stock quantity with colour-coded severity:
 * - green:  at or above parLevel (or parLevel not set)
 * - amber:  below parLevel but at or above reorderPoint
 * - red:    at or below reorderPoint (or quantity ≤ 0)
 *
 * ── On hand is never netted down ────────────────────────────────────────────
 * The chip is ALWAYS on-hand: what is physically there, what a stock count
 * reconciles to, and what valuation multiplies by cost. Subtracting holds from
 * it would make every count report a phantom variance.
 *
 * Holds are shown BESIDE it instead, and only when there are any — most rows
 * have none, and a permanent second number would bury the ones that do. The
 * columns for the full breakdown stay opt-in; this is the at-a-glance signal
 * that a row's on-hand is not all sellable.
 */
const InventoryQuantityBadge: React.FC<Props> = ({
  quantity,
  parLevel,
  reorderPoint,
  reserved,
  size = 'small',
}) => {
  const getColor = (): { color: string; bg: string; tip: string } => {
    if (quantity <= 0) {
      return { color: '#cf222e', bg: '#ffebe9', tip: 'Out of stock' };
    }
    if (reorderPoint !== undefined && quantity <= reorderPoint) {
      return { color: '#cf222e', bg: '#ffebe9', tip: `At or below reorder point (${reorderPoint})` };
    }
    if (parLevel !== undefined && quantity < parLevel) {
      return { color: '#9a6700', bg: '#fef9c3', tip: `Below par level (${parLevel})` };
    }
    return { color: '#1a7f37', bg: '#dafbe1', tip: 'At or above par level' };
  };

  const { color, bg, tip } = getColor();

  const held = reserved != null && reserved > 0 ? reserved : 0;
  const available = quantity - held;
  // Negative available means the holds exceed the stock — an oversell that has
  // already happened. Surfaced rather than clamped to zero: it is the one state
  // here that someone has to act on.
  const oversold = held > 0 && available < 0;

  const tooltip = held > 0
    ? `${formatQty(quantity)} on hand · ${formatQty(held)} reserved · ${formatQty(available)} available`
      + (oversold ? ' — oversold' : ` — ${tip}`)
    : tip;

  const chip = (
    <Chip
      label={formatQty(quantity)}
      size={size}
      sx={{
        fontWeight: 700,
        fontSize: size === 'small' ? '0.75rem' : '0.875rem',
        color,
        backgroundColor: bg,
        border: 'none',
        height: size === 'small' ? 22 : 28,
        minWidth: 36,
      }}
    />
  );

  return (
    <Tooltip title={tooltip} placement="top">
      {held > 0 ? (
        // Stacked rather than inline: the Total Stock column is 104px, and a
        // second figure on the same line pushes the chip out of alignment with
        // every row that has no holds.
        <Box sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1 }}>
          {chip}
          <Typography
            variant="caption"
            sx={{
              mt: 0.25,
              fontSize: '0.6875rem',
              fontWeight: 600,
              color: oversold ? '#cf222e' : '#9a6700',
            }}
          >
            {formatQty(held)} held
          </Typography>
        </Box>
      ) : chip}
    </Tooltip>
  );
};

export default InventoryQuantityBadge;
